/* eslint-disable indent */
/**
 * Custom Tabulator Pagination Module
 * Reusable pagination component using EJS template
 *
 * @package WPCore
 */

import { loadTemplate, templateStore } from '../template/index.js';

const PAGINATION_TEMPLATE = 'tabulator/pagination.ejs';
const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

// Store pagination metadata from server response
const paginationMeta = {
	total: 0,
	pages: 1,
	currentPage: 1
};

/**
 * Initialize custom pagination for a Tabulator table
 * @param {Object} table - Tabulator instance
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.container - Container element for pagination (optional, will create if not provided)
 * @param {HTMLElement} options.tableElement - Table container element (used to insert pagination after)
 * @param {number[]} options.pageSizeOptions - Array of page size options
 * @param {string} options.containerId - ID for the pagination container
 * @returns {Promise<Object>} Pagination controller object
 */
export async function initCustomPagination(table, options = {}) {
	const {
		container = null,
		tableElement = null,
		pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
		containerId = 'custom-pagination'
	} = options;

	// Load template if not already loaded
	await loadTemplate(PAGINATION_TEMPLATE);

	// Get or create pagination container
	const paginationContainer = container || createPaginationContainer(tableElement, containerId);

	// Create controller object
	const controller = {
		container: paginationContainer,
		table,
		pageSizeOptions,

		/**
		 * Render pagination UI
		 */
		render() {
			renderPagination(table, paginationContainer, pageSizeOptions);
		},

		/**
		 * Destroy pagination (remove container and listeners)
		 */
		destroy() {
			if (paginationContainer && paginationContainer.parentNode) {
				paginationContainer.innerHTML = '';
			}
		}
	};

	// Listen to AJAX response to capture pagination metadata
	table.on('dataLoaded', (_data) => {
		// Data from server includes pagination info at response level
		// We need to capture it from ajaxResponse callback
		controller.render();
	});

	// Intercept AJAX response to get pagination metadata
	const originalAjaxResponse = table.options.ajaxResponse;
	table.options.ajaxResponse = function (url, params, response) {
		// Store pagination metadata from server
		if (response) {
			paginationMeta.total = response.total || 0;
			paginationMeta.pages = response.last_page || response.pages || 1;
			paginationMeta.currentPage = response.current_page || params.page || 1;
		}

		// Call original handler if exists
		if (originalAjaxResponse) {
			return originalAjaxResponse.call(this, url, params, response);
		}

		// Default: return data array
		return response.data.data || response;
	};

	// Listen to page events and render pagination
	table.on('pageLoaded', () => {
		controller.render();
	});

	return controller;
}

/**
 * Create pagination container element
 * @param {HTMLElement} tableElement - Table container element
 * @param {string} containerId - ID for the container
 * @returns {HTMLElement} Pagination container element
 */
function createPaginationContainer(tableElement, containerId) {
	let container = document.getElementById(containerId);
	if (!container) {
		container = document.createElement('div');
		container.id = containerId;
		container.className = 'custom-pagination-wrapper';
		if (tableElement && tableElement.parentNode) {
			tableElement.parentNode.insertBefore(container, tableElement.nextSibling);
		}
	}
	return container;
}

/**
 * Render custom pagination using EJS template
 * @param {Object} table - Tabulator instance
 * @param {HTMLElement} container - Pagination container element
 * @param {number[]} pageSizeOptions - Page size options
 */
function renderPagination(table, container, pageSizeOptions) {
	const template = templateStore.get(PAGINATION_TEMPLATE);
	if (!template) {
		console.error('Pagination template not loaded');
		return;
	}

	// Use stored pagination metadata from server response
	const currentPage = table.getPage() || paginationMeta.currentPage || 1;
	const totalPages = paginationMeta.pages || table.getPageMax() || 1;
	const pageSize = table.getPageSize() || 10;
	const totalRows = paginationMeta.total || 0;

	// Render template with data
	// eslint-disable-next-line no-undef
	const html = ejs.render(template, {
		currentPage,
		totalPages,
		pageSize,
		totalRows,
		pageSizeOptions
	});

	container.innerHTML = html;

	// Attach event listeners
	attachPaginationListeners(table, container);
}

/**
 * Attach event listeners to pagination controls
 * @param {Object} table - Tabulator instance
 * @param {HTMLElement} container - Pagination container element
 */
function attachPaginationListeners(table, container) {
	// Page size selector
	const sizeSelect = container.querySelector('[data-action="page-size"]');
	if (sizeSelect) {
		sizeSelect.addEventListener('change', (e) => {
			table.setPageSize(parseInt(e.target.value, 10));
		});
	}

	// Previous button
	const prevBtn = container.querySelector('[data-action="prev"]');
	if (prevBtn) {
		prevBtn.addEventListener('click', () => {
			table.previousPage();
		});
	}

	// Next button
	const nextBtn = container.querySelector('[data-action="next"]');
	if (nextBtn) {
		nextBtn.addEventListener('click', () => {
			table.nextPage();
		});
	}

	// Page number buttons
	const pageButtons = container.querySelectorAll('[data-action="page"]');
	pageButtons.forEach((btn) => {
		btn.addEventListener('click', () => {
			const pageNum = parseInt(btn.dataset.page, 10);
			table.setPage(pageNum);
		});
	});
}

export { PAGINATION_TEMPLATE, DEFAULT_PAGE_SIZE_OPTIONS, paginationMeta };
